import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { useBadgeCertificate, useSaveBadgeCertificate, useIssuerSettings } from '../hooks/useApi';
import {
  buildCertificateSvg,
  fillCertificatePreview,
  parseCertificateOptions,
  CERTIFICATE_TEMPLATES,
  DEFAULT_CERTIFICATE_OPTIONS,
  type CertificateOptions,
  type CertificateTemplateId,
} from '../lib/certificateTemplates';

const ACCENT_PRESETS = ['#7c3aed', '#2563eb', '#059669', '#dc2626', '#d97706', '#0f172a'];

export function CertificateDesigner({
  badge,
  onClose,
}: {
  badge: { id: string; name: string; issuerName?: string; issuerId?: string };
  onClose: () => void;
}) {
  const { t } = useTranslation('dashboard');
  const guid = badge.id.split('/').pop() || badge.id;
  const { data: existingSvg, isLoading } = useBadgeCertificate(guid);
  const { mutate: save, isPending } = useSaveBadgeCertificate();
  const issuerGuid = badge.issuerId ? (badge.issuerId.split('/').pop() ?? null) : null;
  const { data: issuerSettings } = useIssuerSettings(issuerGuid);

  const [options, setOptions] = useState<CertificateOptions>({
    ...DEFAULT_CERTIFICATE_OPTIONS,
    title: DEFAULT_CERTIFICATE_OPTIONS.title,
  });
  const [qrSample, setQrSample] = useState<string>('');
  const [saved, setSaved] = useState(false);
  const seededRef = useRef(false);

  // Close on Escape.
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  // Seed options once: a saved certificate's options win; otherwise default the
  // accent to the issuer's brand color when we know it.
  const settingsResolved = !issuerGuid || issuerSettings !== undefined;
  useEffect(() => {
    if (seededRef.current || existingSvg === undefined || !settingsResolved) return;
    const parsed = parseCertificateOptions(existingSvg);
    if (parsed) setOptions(parsed);
    else if (issuerSettings?.brandColor) setOptions((o) => ({ ...o, accentColor: issuerSettings.brandColor as string }));
    seededRef.current = true;
  }, [existingSvg, settingsResolved, issuerSettings]);

  // Generate a sample QR for the preview.
  useEffect(() => {
    QRCode.toDataURL('https://badges.ninja/verify/sample', { margin: 1, width: 160 })
      .then(setQrSample)
      .catch(() => setQrSample(''));
  }, []);

  const set = <K extends keyof CertificateOptions>(k: K, v: CertificateOptions[K]) => {
    setOptions((o) => ({ ...o, [k]: v }));
    setSaved(false);
  };

  const previewSvg = useMemo(() => {
    const svg = buildCertificateSvg(options);
    return fillCertificatePreview(svg, {
      badgeName: badge.name,
      issuerName: badge.issuerName || 'Your Organization',
      qrDataUri: qrSample,
    });
  }, [options, badge.name, badge.issuerName, qrSample]);

  const hasExisting = !!existingSvg;

  function handleSave() {
    save(
      { badgeId: guid, certificateSvg: buildCertificateSvg(options) },
      { onSuccess: () => { setSaved(true); } }
    );
  }
  function handleRemove() {
    save({ badgeId: guid, certificateSvg: '' }, { onSuccess: () => { setSaved(false); onClose(); } });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-5xl rounded-2xl bg-white shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('certificate.title')}</h3>
            <p className="text-xs text-gray-500">{t('certificate.subtitle', { badge: badge.name })}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label={t('certificate.close')}>
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-0">
          {/* Controls */}
          <div className="border-r border-gray-100 p-5 space-y-5 max-h-[70vh] overflow-y-auto">
            <div>
              <p className="text-xs font-medium text-gray-700 mb-2">{t('certificate.template')}</p>
              <div className="grid grid-cols-3 gap-2">
                {CERTIFICATE_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.id}
                    onClick={() => set('template', tpl.id as CertificateTemplateId)}
                    className={`rounded-lg border px-2 py-3 text-xs font-medium transition-colors ${options.template === tpl.id ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    {t(`certificate.templates.${tpl.id}`)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('certificate.heading')}</label>
              <input
                type="text"
                maxLength={60}
                value={options.title}
                onChange={(e) => set('title', e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
            </div>

            <div>
              <p className="text-xs font-medium text-gray-700 mb-2">{t('certificate.accentColor')}</p>
              <div className="flex items-center gap-2 flex-wrap">
                {ACCENT_PRESETS.map((c) => (
                  <button
                    key={c}
                    onClick={() => set('accentColor', c)}
                    className={`h-7 w-7 rounded-full border-2 transition-transform ${options.accentColor.toLowerCase() === c ? 'border-gray-900 scale-110' : 'border-white shadow'}`}
                    style={{ backgroundColor: c }}
                    aria-label={c}
                  />
                ))}
                <input
                  type="color"
                  value={/^#[0-9a-fA-F]{6}$/.test(options.accentColor) ? options.accentColor : '#7c3aed'}
                  onChange={(e) => set('accentColor', e.target.value)}
                  className="h-7 w-9 rounded border border-gray-200 bg-white p-0.5"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                <input type="checkbox" checked={options.showQr} onChange={(e) => set('showQr', e.target.checked)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-400" />
                <span>{t('certificate.showQr')}</span>
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                <input type="checkbox" checked={options.showLogo} onChange={(e) => set('showLogo', e.target.checked)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-400" />
                <span>{t('certificate.showLogo')}</span>
              </label>
            </div>

            <p className="text-[11px] text-gray-400 leading-relaxed">{t('certificate.mergeHint')}</p>
          </div>

          {/* Preview */}
          <div className="p-5 bg-gray-50 flex flex-col">
            <div className="flex-1 flex items-center justify-center">
              {isLoading ? (
                <div className="w-full aspect-[842/595] rounded-lg bg-gray-100 animate-pulse" />
              ) : (
                <div
                  className="w-full rounded-lg bg-white shadow-md ring-1 ring-gray-200 overflow-hidden [&_svg]:block [&_svg]:w-full [&_svg]:h-auto"
                  // Inline SVG preview. Content is generated from our own templates
                  // + escaped sample data (no untrusted HTML).
                  dangerouslySetInnerHTML={{ __html: previewSvg }}
                />
              )}
            </div>
            <p className="mt-3 text-center text-[11px] text-gray-400">{t('certificate.previewNote')}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
          <div>
            {hasExisting && (
              <button onClick={handleRemove} disabled={isPending} className="rounded-lg border border-red-100 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
                {t('certificate.remove')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {saved && <span className="text-xs font-medium text-emerald-600">{t('certificate.saved')}</span>}
            <button onClick={onClose} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50">{t('certificate.close')}</button>
            <button onClick={handleSave} disabled={isPending} className="rounded-lg bg-primary-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50">
              {isPending ? t('certificate.saving') : t('certificate.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
